import React,{ useState } from 'react';
import { TextField, Typography, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { partial } from 'lodash';
import RubricBlock from '@dyl/blocks/RubricBlock';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import withProps from '@dyl/utils/withProps';
import TaskItems from '@dyl/blocks/TaskItems';
import ImagePicker from "@dyl/components/ImagePicker";
import TaskTemplateSelector from "@dyl/blocks/TaskRadioOptionBlockForPosterAndMessages";
import TaskRadioOptionBlock from "@dyl/blocks/TaskRadioOptionBlock";
import PropTypes from 'prop-types';
import { limitWordsAndSpaces } from '../../../utils/limitWordsAndSpaces';
import { createOnPasteAtCaret } from '@dyl/utils/pasteAtCaret';

export default withProps(TaskItems, { itemComponent: Item });

const useItemStyles = makeStyles(theme => ({
  stimulus: {
    maxWidth: theme.breakpoints.values.sm,
  },
}));

function Item({ index }) {
  const isExample = index === 0;
  const useField = partial(useTaskField, 'items', index);
  const [item] = useField();
  const [selectedTemplates, setSelectedTemplates] = useState({});
  const selectedTemplate = selectedTemplates[index] ?? item.template_type
  const [, setTemplateType] = useField('template_type');

  const handleTemplateChange = (itemIndex, selectedValue) => {
    setSelectedTemplates((prev) => ({
      ...prev,
      [itemIndex]: selectedValue,
    }));
    if(itemIndex === index){
      setTemplateType(selectedValue);
    }
  };

  return (
    <>
      <Typography variant='h6'>
        {isExample ? 'Example' : `Item ${index}`}
      </Typography>

      {isExample && <RubricBlock />}
      {isExample ? <ExampleItem index={index} /> : <TaskTemplateSelector index={index} selectedTemplate={selectedTemplate} onChange={handleTemplateChange} />}
      {selectedTemplate === 'poster' && <PosterItem index={index}/>}
      {selectedTemplate === 'note' && <NoteItem index={index}/>}
      {selectedTemplate === 'phone' && <PhoneItem index={index}/>}
      {selectedTemplate === 'email' && <EmailItem index={index}/>}
    </>
  );
}
Item.propTypes = {
  index: PropTypes.number.isRequired
}

function useTextChangeEvent(setValue, maxWords) {
  return (event) => {
    const value = limitWordsAndSpaces(event.target.value, maxWords);
    setValue(value);
  };
}

useTextChangeEvent.propTypes = {
  setValue: PropTypes.func.isRequired,
  maxWords: PropTypes.number.isRequired
}
function usePasteEvent(setValue, text, maxWords) {
  return createOnPasteAtCaret(setValue, text, maxWords);
}
usePasteEvent.propTypes = {
  setValue: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  maxWords: PropTypes.number.isRequired
}

function ExampleItem({ index }) {
  const classes = useItemStyles();
  const useField = partial(useTaskField, 'items', index);
  const [exampleNote, setExampleNote, exampleNoteError] = useField('note_text');
  const [exampleStimulus, setExampleStimulus, exampleStimulusError] = useField('stimulus_text');
  const onExampleNoteChange = useTextChangeEvent(setExampleNote, 35);
  const onExampleStimulusChange = useTextChangeEvent(setExampleStimulus, 10);
  const [imageId, setImageId, imageError] = useField('background_image');
  const [imageIdNote, setImageIdNote, imageErrorNote] = useField('note_image');
  return (
    <>
      <div style={{ display: 'grid', gridAutoFlow: 'column', marginBottom: '24px' }}>
        <div>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
            Example background image
          </Typography>
          <ImagePicker
            label={`Example background image`}
            value={imageId}
            onChange={setImageId}
            fullWidth
            error={!!imageError}
          />
        </div>
        <div style={{ marginRight: '98px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
            Example note image
          </Typography>
          <ImagePicker
            label={`Example note image`}
            value={imageIdNote}
            onChange={setImageIdNote}
            fullWidth
            error={!!imageErrorNote}
          />
        </div>
      </div>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Example note text
      </Typography>
      <TextField
        className={classes.stimulus}
        value={exampleNote}
        onChange={onExampleNoteChange}
        onPaste={usePasteEvent(setExampleNote, exampleNote, 35)}
        variant='filled'
        fullWidth
        multiline
        helperText={exampleNoteError || 'Maximum 35 words'}
        error={!!exampleNoteError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Example stimulus text
      </Typography>
      <TextField
        className={classes.stimulus}
        value={exampleStimulus}
        onChange={onExampleStimulusChange}
        onPaste={usePasteEvent(setExampleStimulus, exampleStimulus, 10)}
        variant='filled'
        fullWidth
        helperText={exampleStimulusError || 'Maximum 10 words'}
        error={!!exampleStimulusError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <TaskRadioOptionBlock path={['items', index, 'answers']} fieldName={'key_text'} />
    </>
  )
}
ExampleItem.propTypes = {
  index: PropTypes.number.isRequired
}
function PosterItem({ index }) {
  const classes = useItemStyles();
  const useField = partial(useTaskField, 'items', index);
  const [posterTitle, setPosterTitle, posterTitleError] = useField('poster_title');
  const [posterMainText, setPosterMainText, posterMainTextError] = useField('poster_main_text');
  const [posterStimulus, setPosterStimulus, posterStimulusError] = useField('stimulus_text');
  const onPosterTitleChange = useTextChangeEvent(setPosterTitle, 5);
  const onPosterMainTextChange = useTextChangeEvent(setPosterMainText, 25);
  const onPosterStimulusChange = useTextChangeEvent(setPosterStimulus, 10);
  const [imageId, setImageId, imageError] = useField('background_image');
  const [imageIdPoster, setImageIdPoster, imageErrorPoster] = useField('poster_image');
  const [imageIdPosterPicture, setImageIdPosterPicture, imageErrorPosterPicture] = useField('poster_picture_image');
  return (
    <>
      <div style={{ display: 'grid', gridAutoFlow: 'column', marginBottom: '50px', marginTop: '36px' }}>
        <div>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
            {`Item ${index} background image`}
          </Typography>
          <ImagePicker
            label={`Item ${index} background image`}
            value={imageId}
            onChange={setImageId}
            fullWidth
            error={!!imageError}
          />
        </div>
        <div style={{ marginRight: '98px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
            Poster template
          </Typography>
          <ImagePicker
            label={`Poster template`}
            value={imageIdPoster}
            onChange={setImageIdPoster}
            fullWidth
            error={!!imageErrorPoster}
          />
        </div>
      </div>
      <Divider sx={{ marginBottom: '24px' }}/>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Poster title
      </Typography>
      <TextField
        className={classes.stimulus}
        value={posterTitle}
        onChange={onPosterTitleChange}
        onPaste={usePasteEvent(setPosterTitle, posterTitle, 5)}
        variant='filled'
        fullWidth
        multiline
        helperText={posterTitleError || 'Maximum 5 words'}
        error={!!posterTitleError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Picture on poster
      </Typography>
      <div style={{ height: '118px', width: '210px', marginBottom:'24px' }}>
        <ImagePicker
          label={`Picture on poster`}
          value={imageIdPosterPicture}
          onChange={setImageIdPosterPicture}
          fullWidth
          error={!!imageErrorPosterPicture}
        />
      </div>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Poster main text
      </Typography>
      <TextField
        className={classes.stimulus}
        value={posterMainText}
        onChange={onPosterMainTextChange}
        onPaste={usePasteEvent(setPosterMainText, posterMainText, 25)}
        variant='filled'
        fullWidth
        multiline
        helperText={posterMainTextError || 'Maximum 25 words'}
        error={!!posterMainTextError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <Divider sx={{ marginBottom: '36px', marginTop: '12px' }}/>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Stimulus text
      </Typography>
      <TextField
        className={classes.stimulus}
        value={posterStimulus}
        onChange={onPosterStimulusChange}
        onPaste={usePasteEvent(setPosterStimulus, posterStimulus, 10)}
        variant='filled'
        fullWidth
        helperText={posterStimulusError || 'Maximum 10 words'}
        error={!!posterStimulusError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <TaskRadioOptionBlock path={['items', index, 'answers']} fieldName={'key_text'} />
    </>
  )
}
PosterItem.propTypes = {
  index: PropTypes.number.isRequired
}
function NoteItem({ index }) {
  const classes = useItemStyles();
  const useField = partial(useTaskField, 'items', index);
  const [stimulus, setStimulus, stimulusError] = useField('stimulus_text');
  const [note, setNote, noteError] = useField('note_text');
  const onStimulusChange = useTextChangeEvent(setStimulus, 10);
  const onNoteChange = useTextChangeEvent(setNote, 35);
  const [imageId, setImageId, imageError] = useField('background_image');
  const [imageIdNote, setImageIdNote, imageErrorNote] = useField('note_image');
  return (
    <>
      <div style={{ display: 'grid', gridAutoFlow: 'column', marginBottom: '24px' , marginTop: '36px' }}>
        <div>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
          {`Item ${index} background image`}
          </Typography>
          <ImagePicker
            label={`Item ${index} background image`}
            value={imageId}
            onChange={setImageId}
            fullWidth
            error={!!imageError}
          />
        </div>
        <div style={{ marginRight: '98px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
            Note template
          </Typography>
          <ImagePicker
            label={`Note template`}
            value={imageIdNote}
            onChange={setImageIdNote}
            fullWidth
            error={!!imageErrorNote}
          />
        </div>
      </div>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Note text
      </Typography>
      <TextField
        className={classes.stimulus}
        value={note}
        onChange={onNoteChange}
        onPaste={usePasteEvent(setNote, note, 35)}
        variant='filled'
        fullWidth
        multiline
        helperText={noteError || 'Maximum 35 words'}
        error={!!noteError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Stimulus text
      </Typography>
      <TextField
        className={classes.stimulus}
        value={stimulus}
        onChange={onStimulusChange}
        onPaste={usePasteEvent(setStimulus, stimulus, 10)}
        variant='filled'
        fullWidth
        helperText={stimulusError || 'Maximum 10 words'}
        error={!!stimulusError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <TaskRadioOptionBlock path={['items', index, 'answers']} fieldName={'key_text'} />
    </>
  )
}
NoteItem.propTypes = {
  index: PropTypes.number.isRequired
}

function PhoneItem({ index }) {
  const classes = useItemStyles();
  const useField = partial(useTaskField, 'items', index);
  const [stimulus, setStimulus, stimulusError] = useField('stimulus_text');
  const [phone, setPhone, phoneError] = useField('phone_text');
  const onStimulusChange = useTextChangeEvent(setStimulus, 10);
  const onNoteChange = useTextChangeEvent(setPhone, 35);
  const [imageId, setImageId, imageError] = useField('background_image');
  const [imageIdPhone, setImageIdPhone, imageErrorPhone] = useField('phone_image');
  return (
    <>
      <div style={{ display: 'grid', gridAutoFlow: 'column', marginBottom: '24px' , marginTop: '36px' }}>
        <div>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
          {`Item ${index} background image`}
          </Typography>
          <ImagePicker
            label={`Item ${index} background image`}
            value={imageId}
            onChange={setImageId}
            fullWidth
            error={!!imageError}
          />
        </div>
        <div style={{ marginRight: '98px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
            Phone template
          </Typography>
          <ImagePicker
            label={`Phone template`}
            value={imageIdPhone}
            onChange={setImageIdPhone}
            fullWidth
            error={!!imageErrorPhone}
          />
        </div>
      </div>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Phone message text
      </Typography>
      <TextField
        className={classes.stimulus}
        value={phone}
        onChange={onNoteChange}
        onPaste={usePasteEvent(setPhone, phone, 35)}
        variant='filled'
        fullWidth
        multiline
        helperText={phoneError || 'Maximum 35 words'}
        error={!!phoneError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Stimulus text
      </Typography>
      <TextField
        className={classes.stimulus}
        value={stimulus}
        onChange={onStimulusChange}
        onPaste={usePasteEvent(setStimulus, stimulus, 10)}
        variant='filled'
        fullWidth
        helperText={stimulusError || 'Maximum 10 words'}
        error={!!stimulusError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <TaskRadioOptionBlock path={['items', index, 'answers']} fieldName={'key_text'} />
    </>
  )
}
PhoneItem.propTypes = {
  index: PropTypes.number.isRequired
}

function EmailItem({ index }) {
  const classes = useItemStyles();
  const useField = partial(useTaskField, 'items', index);
  const [headerLine, setHeaderLine, headerLineError] = useField('email_header_line');
  const [emailTo, setEmailTo, emailToError] = useField('email_to_id');
  const [emailFrom, setEmailFrom, emailFromError] = useField('email_from_id');
  const [emailMessage, setEmailMessage, emailMessageError] = useField('email_message');
  const [stimulus, setStimulus, stimulusError] = useField('stimulus_text');
  const [imageId, setImageId, imageError] = useField('background_image');
  const [imageIdEmail, setImageIdEmail, imageErrorEmail] = useField('email_image');
  // Removed unused answersField, answers, setAnswers

  // Handlers for word/char limits
  const onHeaderLineChange = useTextChangeEvent(setHeaderLine, 5);
  const onEmailToChange = e => {
    if (e.target.value.length <= 25) setEmailTo(e.target.value);
  };
  const onEmailFromChange = e => {
    if (e.target.value.length <= 25) setEmailFrom(e.target.value);
  };
  const onEmailMessageChange = useTextChangeEvent(setEmailMessage, 35);
  const onStimulusChange = useTextChangeEvent(setStimulus, 10);

  return (
    <>
      <div style={{ display: 'grid', gridAutoFlow: 'column', marginBottom: '24px', marginTop: '36px' }}>
        <div>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
            {`Item ${index} background image`}
          </Typography>
          <ImagePicker
            label={`Item ${index} background image`}
            value={imageId}
            onChange={setImageId}
            fullWidth
            error={!!imageError}
          />
        </div>
        <div style={{ marginRight: '98px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
            Email template
          </Typography>
          <ImagePicker
            label={`Email template`}
            value={imageIdEmail}
            onChange={setImageIdEmail}
            fullWidth
            error={!!imageErrorEmail}
          />
        </div>
      </div>
      <Divider sx={{ marginBottom: '24px' }}/>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
        Email header line
      </Typography>
      <TextField
        className={classes.stimulus}
        value={headerLine}
        onChange={onHeaderLineChange}
        onPaste={usePasteEvent(setHeaderLine, headerLine, 5)}
        variant='filled'
        fullWidth
        helperText={headerLineError || 'Maximum 5 words'}
        error={!!headerLineError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <div style={{ display: 'flex', gap: '24px', marginTop: '12px', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>Email To:</Typography>
          <TextField
            value={emailTo}
            onChange={onEmailToChange}
            variant='filled'
            fullWidth
            helperText={emailToError || 'Maximum 25 characters'}
            error={!!emailToError}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>Email From:</Typography>
          <TextField
            value={emailFrom}
            onChange={onEmailFromChange}
            variant='filled'
            fullWidth
            helperText={emailFromError || 'Maximum 25 characters'}
            error={!!emailFromError}
          />
        </div>
      </div>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>Email message</Typography>
      <TextField
        className={classes.stimulus}
        value={emailMessage}
        onChange={onEmailMessageChange}
        onPaste={usePasteEvent(setEmailMessage, emailMessage, 35)}
        variant='filled'
        fullWidth
        multiline
        helperText={emailMessageError || 'Maximum 35 words'}
        error={!!emailMessageError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <Divider sx={{ marginBottom: '24px', marginTop: '24px' }}/>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>Stimulus text</Typography>
      <TextField
        className={classes.stimulus}
        value={stimulus}
        onChange={onStimulusChange}
        onPaste={usePasteEvent(setStimulus, stimulus, 10)}
        variant='filled'
        fullWidth
        helperText={stimulusError || 'Maximum 10 words'}
        error={!!stimulusError}
        sx={{ '& .MuiInputBase-input' : { width: '652px' }, '& .MuiInputBase-root' : { width: '652px' }}}
      />
      <Divider sx={{ marginBottom: '24px', marginTop: '24px' }}/>
      {/* Custom radio options with 12 word limit */}
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>Options</Typography>
      <TaskRadioOptionBlock path={['items', index, 'answers']} fieldName={'key_text'} helperText={'Maximum 12 words'} maxWords={12} />
    </>
  );
}
EmailItem.propTypes = {
  index: PropTypes.number.isRequired
}

